import React, { useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, apiDelete, formatDate } from '../utils/api';
import AdminForm from './AdminForm';
import Pagination from './Pagination';

const Admins = () => {
  const { print_lang } = useLanguage();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const fetchAdmins = async () => {
    try {
      const data = await apiGet('/api/admin/all');
      if (data.success) {
        setAdmins(data.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setShowModal(true);
  };

  const handleDelete = async (admin) => {
    if (admin.admin_id === 1) {
      alert('Cannot delete Super Admin');
      return;
    }

    if (!window.confirm(print_lang('common.confirmDelete', 'Are you sure you want to delete?'))) {
      return;
    }

    try {
      const data = await apiDelete(`/api/admin/${admin.admin_id}`);
      if (data.success) {
        fetchAdmins();
      } else {
        alert(data.message || 'Delete failed');
      }
    } catch (err) {
      alert(err.message || 'Failed to delete admin');
    }
  };

  const handleSave = () => {
    setShowModal(false);
    fetchAdmins();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'badge-success', label: print_lang('common.active', 'Active') },
      pending: { class: 'badge-warning', label: print_lang('common.pending', 'Pending') },
      inactive: { class: 'badge-danger', label: print_lang('common.inactive', 'Inactive') },
    };
    const s = statusMap[status] || { class: 'badge-info', label: status };
    return <span className={`badge ${s.class}`}>{s.label}</span>;
  };

  // Filter admins
  const filteredAdmins = admins.filter(admin => {
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!admin.name?.toLowerCase().includes(search) && !admin.email?.toLowerCase().includes(search)) {
        return false;
      }
    }
    // Filter by status
    if (filterStatus && admin.status !== filterStatus) {
      return false;
    }
    return true;
  });

  // Paginate
  const totalCount = filteredAdmins.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{print_lang('nav.admins', 'Admins')}</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <strong>Note:</strong> Admins register themselves via the signup page. You can edit their details and manage their status here.
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="grid-2">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('common.search', 'Search')}</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('common.filter', 'Filter')} {print_lang('admin.status', 'Status')}</label>
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">{print_lang('common.all', 'All')}</option>
              <option value="active">{print_lang('common.active', 'Active')}</option>
              <option value="pending">{print_lang('common.pending', 'Pending')}</option>
              <option value="inactive">{print_lang('common.inactive', 'Inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {paginatedAdmins.length === 0 && !searchTerm && !filterStatus ? (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
            <p className="empty-state-text">No admins registered yet</p>
          </div>
        ) : paginatedAdmins.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
            <p className="empty-state-text">No admins match your search</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{print_lang('admin.name', 'Name')}</th>
                    <th>{print_lang('admin.email', 'Email')}</th>
                    <th>{print_lang('admin.status', 'Status')}</th>
                    <th>{print_lang('admin.licenseExpiry', 'License Expiry')}</th>
                    <th>{print_lang('admin.createdAt', 'Created At')}</th>
                    <th>{print_lang('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAdmins.map((admin) => (
                    <tr key={admin.admin_id}>
                      <td>
                        {admin.admin_id}
                        {admin.admin_id === 1 && (
                          <span className="super-admin-badge" style={{ marginLeft: '0.5rem' }}>
                            {print_lang('admin.superAdmin', 'Super Admin')}
                          </span>
                        )}
                      </td>
                      <td>
                        <strong>{admin.name}</strong>
                      </td>
                      <td>{admin.email}</td>
                      <td>{getStatusBadge(admin.status)}</td>
                      <td>
                        {admin.license_expired ? (
                          <span style={{
                            color: new Date(admin.license_expired) < new Date() ? '#ef4444' :
                              new Date(admin.license_expired) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) ? '#f59e0b' : '#10b981'
                          }}>
                            {formatDate(admin.license_expired)}
                          </span>
                        ) : '-'}
                      </td>
                      <td>{formatDate(admin.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => handleEdit(admin)} title={print_lang('common.edit', 'Edit')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          {admin.admin_id !== 1 && (
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(admin)} title={print_lang('common.delete', 'Delete')}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </div>

      {showModal && (
        <AdminForm
          admin={editingAdmin}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Admins;
